import {
  Avatar,
  Badge,
  BadgeProps,
  Box,
  BoxProps,
  Button,
  Center,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputGroupProps,
  InputProps,
  Popover,
  PopoverContentProps,
  PopoverRootProps as PopoverProps,
  Portal,
  Spinner,
  Stack,
  StackProps,
  Text,
  VStack,
  Separator,
} from '@chakra-ui/react'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { CloseIcon, SearchIcon } from '@src/components/sco/icons'
import React, { createRef, ReactNode, useEffect, useMemo, useState } from 'react'
import { LuPlus, LuX } from 'react-icons/lu'

export type ComboboxItem = {
  key: string
  title?: string
  pictureUrl?: string
  pictureName?: string
  userId?: string
  badgeProps?: BadgeProps
}

export type ComboboxTagInputProps = {
  allowMultipleSelections?: boolean
  allowAddingItems?: boolean
  allowNonMatchingItems?: boolean
  blurOnSelect?: boolean
  boxProps?: BoxProps
  defaultSelectedItems?: ComboboxItem[]
  delayLoadingState?: boolean
  hideItemsOnBlank?: boolean
  hideItemsAfterSelect?: boolean
  icon?: any
  inputGroupProps?: InputGroupProps
  inputProps?: InputProps
  items?: ComboboxItem[]
  itemsAreFiltered?: boolean
  itemsAreLoading?: boolean
  maxSelections?: number
  onChange?: (selectedItems: ComboboxItem[]) => any
  onClear?: () => void
  onInputChange?: (value: string) => any
  placeholder?: string
  popoverProps?: Partial<PopoverProps>
  popoverContentProps?: PopoverContentProps
  renderSelectedItems?:
    ((selectedItems: ComboboxItem[], handleRemoveItem?: (index: number) => void) => ReactNode) | boolean
  tagStackProps?: StackProps
  selectedItemBoxProps?: BoxProps
  optionItemBoxProps?: BoxProps
  /** When set, the combobox is controlled by the parent. */
  value?: ComboboxItem[]
}

export const ComboboxTagInput = (props: ComboboxTagInputProps) => {
  const inputRef = createRef<HTMLInputElement>()
  const [inputValue, setInputValue] = useState<string>(
    !props.allowMultipleSelections && props.defaultSelectedItems
      ? props.defaultSelectedItems.map(i => i.title).join(', ')
      : ''
  )
  const [hideItems, setHideItems] = useState<boolean>(false)
  const [inputIsFocused, setInputIsFocused] = useState<boolean>(false)
  const [selectedItems, setSelectedItems] = useState<ComboboxItem[]>(
    props.value || props.defaultSelectedItems || []
  )
  const [selectedMatch, setSelectedMatch] = useState<ComboboxItem>(null)
  const [addedItems, setAddedItems] = useState<ComboboxItem[]>([])
  const [items, setItems] = useState<ComboboxItem[]>(props.items || [])

  useEffect(() => {
    if (inputValue && hideItems) {
      setHideItems(false)
    }
  }, [inputValue])

  useEffect(() => {
    if (
      !props.allowMultipleSelections &&
      props.renderSelectedItems !== false &&
      selectedItems.length > 1 &&
      selectedItems[0].title !== inputValue
    ) {
      setSelectedItems([])
    }

    if (props.onInputChange) {
      props.onInputChange(inputValue)
    }
  }, [inputValue])

  useEffect(() => {
    if (props.onChange) {
      props.onChange(selectedItems)
    }
  }, [JSON.stringify(selectedItems)])

  useEffect(() => {
    if (!props.value) {
      return
    }
    const current = selectedItems.map(item => `${item.key}:${item.title || ''}`).join('\0')
    const next = props.value.map(item => `${item.key}:${item.title || ''}`).join('\0')
    if (current !== next) {
      setSelectedItems(props.value)
    }
  }, [props.value?.map(item => `${item.key}:${item.title || ''}`).join('\0')])

  useEffect(() => {
    const nextItems = props.items || []
    if (nextItems.length === 0 && props.delayLoadingState && props.itemsAreLoading) {
      return
    }
    setItems([...nextItems, ...addedItems])
    if (addedItems?.length > 0) {
      setSelectedItems(prev => {
        const next = props.allowMultipleSelections ? [...prev, ...addedItems] : [addedItems[0]]
        return typeof props.maxSelections === 'number' ? next.slice(0, props.maxSelections) : next
      })
    }
  }, [JSON.stringify(props.items), JSON.stringify(addedItems)])

  const filteredItems = useMemo(() => {
    const list = items || []
    if (props.itemsAreFiltered) {
      setSelectedMatch(list[0] || null)
      return list
    }

    let next: ComboboxItem[] = []
    if (inputValue) {
      next = list.filter(item => (item.title || '').toLowerCase().indexOf(inputValue.toLowerCase()) !== -1)
      setSelectedMatch(next[0] || null)
    } else {
      next = list
      setSelectedMatch(null)
    }

    return next
  }, [items, inputValue, props.itemsAreFiltered])

  const atMax = typeof props.maxSelections === 'number' && selectedItems.length >= props.maxSelections

  const handleAddItem = (item: ComboboxItem) => {
    if (props.allowMultipleSelections) {
      if (atMax || selectedItems.find(i => i.key == item.key)) {
        return
      }
      const newValue = [...selectedItems, item]
      setSelectedItems(newValue)
      setSelectedMatch(null)
      setInputValue('')
      if (props.blurOnSelect) {
        inputRef.current?.blur()
      } else if (props.hideItemsAfterSelect) {
        setHideItems(true)
      }
    } else {
      setSelectedItems([item])
      setSelectedMatch(null)
      setInputValue(item.title)
      inputRef.current?.blur()
    }
  }

  const handleRemoveItem = (index: number) => {
    const newValue = [...selectedItems]
    newValue.splice(index, 1)
    setSelectedItems(newValue)
    if (!props.allowMultipleSelections) {
      setInputValue('')
      inputRef.current?.focus()
    }
  }

  const handleClear = () => {
    setSelectedItems([])
    setInputValue('')
    inputRef.current?.focus()
  }

  const popoverIsOpen = inputIsFocused && (!props.hideItemsOnBlank || !!inputValue)

  const showInput = !(
    props.allowMultipleSelections === false &&
    props.renderSelectedItems &&
    selectedItems.length > 0
  )

  return (
    <Box width={'full'} {...props.boxProps}>
      {props.renderSelectedItems !== false && selectedItems.length > 0 && (
        <Stack align={'stretch'} direction={'column'} gap={0} marginBottom={1} {...props.tagStackProps}>
          {typeof props.renderSelectedItems === 'function'
            ? props.renderSelectedItems(selectedItems, handleRemoveItem)
            : selectedItems.map((item: ComboboxItem, index: number) => (
                <Box
                  key={index}
                  bgColor={'colorPalette.bg'}
                  border={'1px solid {colors.colorPalette.border/50}'}
                  borderRadius={'md'}
                  display={'block'}
                  fontSize={'sm'}
                  lineHeight={1}
                  paddingX={4}
                  paddingY={3}
                  {...props.selectedItemBoxProps}
                >
                  <HStack width={'full'}>
                    {(item.pictureUrl || item.pictureName) && (
                      <Avatar.Root size={'xs'}>
                        <Avatar.Fallback name={item.pictureName} />
                        <Avatar.Image src={item.pictureUrl} />
                      </Avatar.Root>
                    )}
                    <Text as={'span'} color={'colorPalette.fg/90'} fontWeight={'bold'} width={'full'}>
                      {item.title || item.key}
                      {item.badgeProps && <Badge {...item.badgeProps} />}
                    </Text>
                    <IconButton
                      aria-label={`Remove ${item.title || item.key}`}
                      size={'sm'}
                      variant={'outline'}
                      onClick={() => handleRemoveItem(index)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </HStack>
                </Box>
              ))}
        </Stack>
      )}
      {showInput && (
        <Popover.Root
          autoFocus={false}
          open={popoverIsOpen && !hideItems}
          size={'sm'}
          {...props.popoverProps}
          positioning={{
            placement: 'bottom',
            sameWidth: true,
          }}
        >
          <Popover.Trigger asChild>
            <InputGroup
              endElement={
                !props.allowMultipleSelections && (inputValue || selectedItems?.length > 0) ? (
                  <IconButton aria-label={'Clear'} size={'xs'} variant={'outline'} onClick={handleClear}>
                    <LuX />
                  </IconButton>
                ) : undefined
              }
              startElement={
                props.icon !== false ? (
                  <Icon as={props.icon || SearchIcon} color={'colorPalette.fg'} />
                ) : undefined
              }
            >
              <Input
                ref={inputRef}
                autoComplete={'off'}
                name={'filter'}
                paddingLeft={8}
                placeholder={props.placeholder || 'Search options...'}
                type={'text'}
                value={inputValue}
                onBlur={() => setInputIsFocused(false)}
                onFocus={() => setInputIsFocused(true)}
                onChange={ev => {
                  setInputValue(ev.target.value)
                }}
                onKeyDown={ev => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault()
                    if (selectedMatch) {
                      handleAddItem(selectedMatch)
                    } else {
                      if (props.allowAddingItems && !atMax && inputValue.trim()) {
                        setAddedItems([{ key: `new-item-${addedItems?.length || 0}`, title: inputValue }])
                      }
                      inputRef.current?.blur()
                    }
                  }
                  if (ev.key === 'ArrowDown') {
                    ev.preventDefault()
                    const selectedIndex = filteredItems.findIndex(i => i.key == selectedMatch?.key) || 0
                    setSelectedMatch(filteredItems[selectedIndex + 1])
                  }
                  if (ev.key === 'ArrowUp') {
                    ev.preventDefault()
                    const selectedIndex = filteredItems.findIndex(i => i.key == selectedMatch?.key) || 0
                    setSelectedMatch(filteredItems[selectedIndex - 1 || 0])
                  }
                  if (ev.key === 'Escape') {
                    inputRef.current?.blur()
                  }
                  if (ev.key === 'Backspace' && !inputValue) {
                    if (selectedItems.length === 0) {
                      inputRef.current?.blur()
                    } else {
                      setSelectedItems(prev => prev.slice(0, -1))
                    }
                  }
                }}
                {...props.inputProps}
              />
            </InputGroup>
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content
                autoFocus={false}
                backdropFilter={'blur(20px)'}
                bgColor={'colorPalette.bg'}
                width={'full'}
                colorPalette={{
                  _dark: 'purple',
                  _light: 'orange',
                }}
                {...props.popoverContentProps}
              >
                <Popover.Arrow bgColor={'colorPalette.bg'} />
                <VStack gap={'1px'} maxHeight={'35vh'} overflow={'auto'} padding={1}>
                  {props.itemsAreLoading && !props.delayLoadingState && (
                    <Center padding={4}>
                      <Spinner color={'colorPalette.fg/90'} size={'sm'} />
                    </Center>
                  )}
                  <Separator borderColor={'colorPalette.border'} />
                  {!props.itemsAreLoading && filteredItems.length === 0 && (
                    <>
                      {inputValue && props.allowAddingItems && !atMax ? (
                        <Button
                          size={'sm'}
                          type={'button'}
                          variant={'ghost'}
                          width={'full'}
                          // Use mousedown so selection runs before the input blurs and closes the popover.
                          // onClick never fires once blur unmounts the list.
                          onMouseDown={ev => {
                            ev.preventDefault()
                            ev.stopPropagation()
                            setAddedItems(prev => [
                              ...prev,
                              { key: `new-item-${prev.length}`, title: inputValue },
                            ])
                          }}
                        >
                          <LuPlus />
                          Add "{inputValue}"
                        </Button>
                      ) : (
                        <Text fontSize={'11px'} padding={4}>
                          No Matches
                        </Text>
                      )}
                    </>
                  )}
                  <Separator borderColor={'colorPalette.border'} />
                  {filteredItems.map((item: ComboboxItem, index: number) => (
                    <Box key={index} width={'full'}>
                      <Button
                        bgColor={item.key == selectedMatch?.key ? 'colorPalette.emphasized' : 'transparent'}
                        borderRadius={'0.25em'}
                        height={'auto'}
                        justifyContent={'flex-start'}
                        // Use mousedown so selection runs before the input blurs and closes the popover.
                        // onClick never fires once blur unmounts the list (keyboard Enter still works).
                        padding={'0.33em 0.5em'}
                        size={'sm'}
                        title={`Select: ${item.title || item.key}`}
                        type={'button'}
                        variant={'ghost'}
                        width={'full'}
                        _focus={{
                          bgColor: 'colorPalette.emphasized',
                        }}
                        _hover={{
                          bgColor: 'colorPalette.emphasized',
                        }}
                        css={{
                          '& mark': {
                            bgColor: 'colorPalette.subtle',
                            color: 'colorPalette.fg',
                          },
                        }}
                        onMouseDown={ev => {
                          ev.preventDefault()
                          ev.stopPropagation()
                          handleAddItem(item)
                        }}
                      >
                        <HStack gap={2} width={'full'}>
                          {item.userId && <UserAvatar avatarProps={{ size: 'xs' }} userId={item.userId} />}
                          {!item.userId && (item.pictureName || item.pictureUrl) && (
                            <Avatar.Root size={'xs'}>
                              <Avatar.Fallback name={item.pictureName} />
                              <Avatar.Image src={item.pictureUrl} />
                            </Avatar.Root>
                          )}
                          {inputValue &&
                          item?.title &&
                          item.title.toLowerCase().includes(inputValue.toLowerCase()) ? (
                            <span>
                              {item.title.split(new RegExp(`(${inputValue})`, 'gi')).map((part, index) => (
                                <Text
                                  key={index}
                                  as={part.toLowerCase() === inputValue.toLowerCase() ? 'mark' : 'span'}
                                >
                                  {part}
                                </Text>
                              ))}
                            </span>
                          ) : (
                            <Text as={'span'}>{item.title || item.key} </Text>
                          )}
                          {item.badgeProps && <Badge {...item.badgeProps} />}
                        </HStack>
                      </Button>
                    </Box>
                  ))}
                </VStack>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
      )}
    </Box>
  )
}
